import { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { deleteUser } from '@/lib/firebase/admin/user/deleteUser';
import { getUserCustomerOnlyOrganizations } from '@/lib/firebase/queries/organizations';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { Loader2, Pencil, Trash2, Settings, Building2, Plus, ArrowUpDown, ArrowUp, ArrowDown, Search } from 'lucide-react';
import { toast } from 'sonner';
import { UserRoleDialog } from './UserRoleDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useLanguage } from '@/contexts/LanguageContext';
import type { User } from '@/types';
import type { Organization } from '@/types/organization';

interface UserWithOrganizations extends User {
  organizations: Organization[];
}

type SortColumn = 'name' | 'phone' | 'roles' | 'organizations' | 'joined';
type SortDirection = 'asc' | 'desc' | null;

interface UserListProps {
  searchQuery?: string;
  roleFilter?: string[];
  organizationFilter?: string[];
}

export function UserList({ searchQuery = '', roleFilter = [], organizationFilter = [] }: UserListProps) {
  const [users, setUsers] = useState<UserWithOrganizations[]>([]);
  const [isLoading, setIsLoading] = useState(false); // Changed to false - don't load initially
  const [hasSearched, setHasSearched] = useState(false); // Track if user has searched/filtered
  const [selectedUser, setSelectedUser] = useState<UserWithOrganizations | null>(null);
  const [userToDelete, setUserToDelete] = useState<UserWithOrganizations | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [userToAddToOrg, setUserToAddToOrg] = useState<UserWithOrganizations | null>(null);
  const [availableOrganizations, setAvailableOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [isAddingToOrg, setIsAddingToOrg] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const { t, dir } = useLanguage();

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const usersQuery = query(collection(db, 'users'));
      const snapshot = await getDocs(usersQuery);
      
      // Fetch users with their organizations and habit counts
      const usersData = await Promise.all(
        snapshot.docs.map(async (doc) => {
          const userData = {
        id: doc.id,
        ...doc.data()
          } as User;

          // Get organizations where user has ONLY customer role
          const organizations = await getUserCustomerOnlyOrganizations(userData.id);
          
          return {
            ...userData,
            organizations: organizations || []
          } as UserWithOrganizations;
        })
      );
      
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  };

  // Only fetch users when there's a search query, role filter, or organization filter
  useEffect(() => {
    const shouldFetch = searchQuery.trim().length >= 2 || roleFilter.length > 0 || organizationFilter.length > 0;
    
    if (shouldFetch) {
      setHasSearched(true);
      fetchUsers();
    } else if (hasSearched && searchQuery.trim().length === 0 && roleFilter.length === 0 && organizationFilter.length === 0) {
      // Clear results when all filters are removed
      setUsers([]);
      setHasSearched(false);
    }
  }, [searchQuery, roleFilter, organizationFilter]);

  useEffect(() => {
    if (userToAddToOrg) {
      fetchAvailableOrganizations();
    }
  }, [userToAddToOrg]);

  const handleDelete = async () => {
    if (!userToDelete) return;

    try {
      setIsDeleting(true);
      const result = await deleteUser(userToDelete.id);
      
      if (result.success) {
        setUsers(prev => prev.filter(user => user.id !== userToDelete.id));
        toast.success(t('dashboard.admin.users.delete.success'));
        setUserToDelete(null);
      } else {
        toast.error(result.error || t('dashboard.admin.users.delete.error'));
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(t('dashboard.admin.users.delete.error'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRoleUpdate = async () => {
    await fetchUsers(); // Refresh the user list after role update
  };

  const getRoleName = (role: string) => {
    return t(`dashboard.admin.users.roles.${role.toLowerCase()}`);
  };

  const fetchAvailableOrganizations = async () => {
    try {
      const orgsQuery = query(collection(db, 'organizations'));
      const orgsSnapshot = await getDocs(orgsQuery);
      const orgs = orgsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Organization[];
      setAvailableOrganizations(orgs);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Failed to fetch organizations');
    }
  };

  const handleAddToOrganization = async () => {
    if (!userToAddToOrg || !selectedOrgId) return;

    try {
      setIsAddingToOrg(true);
      
      // Add user to organization as customer with deterministic ID
      const memberId = `${selectedOrgId}_${userToAddToOrg.id}_customer`;
      const memberRef = doc(collection(db, 'organizationMembers'), memberId);
      const existing = await getDoc(memberRef);
      if (existing.exists()) {
        await setDoc(memberRef, { role: 'customer', addedBy: 'admin', updatedAt: Timestamp.now() }, { merge: true });
      } else {
        await setDoc(memberRef, {
          userId: userToAddToOrg.id,
          organizationId: selectedOrgId,
          role: 'customer',
          addedAt: Timestamp.now(),
          addedBy: 'admin', // TODO: replace with current admin ID
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      }
      
      // Note: Organization default habits system has been removed
      
      // Refresh the user list to show the new organization
      await fetchUsers();
      
      toast.success(
        dir === 'rtl'
          ? `تم إضافة ${userToAddToOrg.firstNameEn} إلى المنظمة`
          : `Successfully added ${userToAddToOrg.firstNameEn} to organization`
      );
      
      setUserToAddToOrg(null);
      setSelectedOrgId('');
    } catch (error) {
      console.error('Error adding user to organization:', error);
      toast.error(
        dir === 'rtl'
          ? 'فشل في إضافة المستخدم إلى المنظمة'
          : 'Failed to add user to organization'
      );
    } finally {
      setIsAddingToOrg(false);
    }
  };


  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortColumn(null);
      } else {
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedUsers = () => {
    if (!sortColumn || !sortDirection) {
      return filteredUsers;
    }

    return [...filteredUsers].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'name':
          aValue = dir === 'rtl' 
            ? `${a.firstNameAr || a.firstNameEn} ${a.lastNameAr || a.lastNameEn}`
            : `${a.firstNameEn} ${a.lastNameEn}`;
          bValue = dir === 'rtl'
            ? `${b.firstNameAr || b.firstNameEn} ${b.lastNameAr || b.lastNameEn}`
            : `${b.firstNameEn} ${b.lastNameEn}`;
          break;
        case 'phone':
          // Convert phone numbers to numbers for proper sorting, fallback to string comparison
          const aPhone = a.phone?.replace(/\D/g, '') || '0';
          const bPhone = b.phone?.replace(/\D/g, '') || '0';
          aValue = parseInt(aPhone) || 0;
          bValue = parseInt(bPhone) || 0;
          break;
        case 'roles':
          // Sort by number of active roles, then by role names
          const aRoles = a.roles?.filter(r => r.isActive).map(r => r.role).sort().join(', ') || '';
          const bRoles = b.roles?.filter(r => r.isActive).map(r => r.role).sort().join(', ') || '';
          aValue = aRoles;
          bValue = bRoles;
          break;
        case 'organizations':
          // Sort by number of organizations, then by organization names
          const aOrgs = a.organizations?.map(org => org.name).sort().join(', ') || '';
          const bOrgs = b.organizations?.map(org => org.name).sort().join(', ') || '';
          aValue = aOrgs;
          bValue = bOrgs;
          break;
                  case 'joined':
            aValue = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
            bValue = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
            break;
          default:
            return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    if (sortDirection === 'asc') return <ArrowUp className="h-4 w-4 text-gray-400" />;
    if (sortDirection === 'desc') return <ArrowDown className="h-4 w-4 text-gray-400" />;
    return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
  };

  // Filter users based on search query, role filter, and organization filter
  const filteredUsers = users.filter(user => {
    const fullNameEn = `${user.firstNameEn} ${user.lastNameEn}`.toLowerCase();
    const fullNameAr = `${user.firstNameAr} ${user.lastNameAr}`.toLowerCase();
    const email = user.email?.toLowerCase() || '';
    const phone = user.phone?.toLowerCase() || '';
    
    const matchesSearch = searchQuery === '' || 
      fullNameEn.includes(searchQuery.toLowerCase()) ||
      fullNameAr.includes(searchQuery.toLowerCase()) ||
      email.includes(searchQuery.toLowerCase()) ||
      phone.includes(searchQuery.toLowerCase());
    
    const matchesRoleFilter = roleFilter.length === 0 || 
      user.roles.some(role => roleFilter.includes(role.role));
    
    const matchesOrganizationFilter = organizationFilter.length === 0 || 
      user.organizations.some(org => organizationFilter.includes(org.id));
    
    return matchesSearch && matchesRoleFilter && matchesOrganizationFilter;
  });

  const sortedUsers = getSortedUsers();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Show instructions when no search/filter is applied
  if (!hasSearched) {
    return (
      <div className="space-y-4" dir={dir}>
        <div className="rounded-md border bg-muted/50 p-8 text-center">
          <div className="mx-auto max-w-md space-y-4">
            <h3 className="text-lg font-semibold">
              {t('dashboard.admin.users.instructions.title')}
            </h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" dir={dir}>
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('name')}
                  className={`h-auto p-0 font-semibold hover:bg-transparent gap-2 flex-row-reverse ${
                    sortColumn === 'name' ? 'text-primary' : ''
                  }`}
                >
                  {getSortIcon('name')}
                  {t('dashboard.admin.users.table.name')}
                </Button>
              </TableHead>
              <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('phone')}
                  className={`h-auto p-0 font-semibold hover:bg-transparent gap-2 flex-row-reverse ${
                    sortColumn === 'phone' ? 'text-primary' : ''
                  }`}
                >
                  {getSortIcon('phone')}
                  {t('dashboard.admin.users.table.phone')}
                </Button>
              </TableHead>
              <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('roles')}
                  className={`h-auto p-0 font-semibold hover:bg-transparent gap-2 flex-row-reverse ${
                    sortColumn === 'roles' ? 'text-primary' : ''
                  }`}
                >
                  {getSortIcon('roles')}
                  {t('dashboard.admin.users.table.roles')}
                </Button>
              </TableHead>
              <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('organizations')}
                  className={`h-auto p-0 font-semibold hover:bg-transparent gap-2 flex-row-reverse ${
                    sortColumn === 'organizations' ? 'text-primary' : ''
                  }`}
                >
                  {getSortIcon('organizations')}
                  {t('dashboard.admin.users.table.organizations')}
                </Button>
              </TableHead>
              <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('joined')}
                  className={`h-auto p-0 font-semibold hover:bg-transparent gap-2 flex-row-reverse ${
                    sortColumn === 'joined' ? 'text-primary' : ''
                  }`}
                >
                  {getSortIcon('joined')}
                  {t('dashboard.admin.users.table.joined')}
                </Button>
              </TableHead>
              <TableHead className="w-12">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedUsers.map((user, index) => (
              <TableRow key={`${user.id}-${index}`}>
                <TableCell className="font-medium">
                  {dir === 'rtl' 
                    ? `${user.firstNameAr || user.firstNameEn || ''} ${user.lastNameAr || user.lastNameEn || ''}`.trim() || 'Unknown'
                    : `${user.firstNameEn || user.firstNameAr || ''} ${user.lastNameEn || user.lastNameAr || ''}`.trim() || 'Unknown'
                  }
                </TableCell>
                <TableCell>{user.phone || t('dashboard.admin.users.table.noPhone')}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {user.roles
                      ?.filter(r => r.isActive)
                      .map((role, index) => (
                        <Badge key={index} variant="secondary">
                          {getRoleName(role.role)}
                        </Badge>
                      )) || []}
                  </div>
                </TableCell>
                <TableCell>
                  {user.organizations && user.organizations.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {user.organizations.map((org) => (
                        <Badge key={org.id} variant="outline" className="text-xs">
                          <Building2 className="h-3 w-3 mr-1" />
                          {org.name}
                        </Badge>
                      ))}
                  </div>
                  ) : (
                    <Button 
                      size="sm" 
                      className="bg-[#7dda55] hover:bg-[#6dc447] text-white rounded-full text-xs px-2 py-1 h-6"
                      onClick={() => setUserToAddToOrg(user)}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      {t('dashboard.admin.users.organizations.addToOrganization')}
                    </Button>
                  )}
                </TableCell>
                <TableCell>
                  {formatDistanceToNow(user.createdAt?.toDate?.() || user.createdAt || new Date(), { addSuffix: true })}
                </TableCell>

                <TableCell className={dir === 'rtl' ? 'text-left' : 'text-right'}>
                  <div className={`flex ${dir === 'rtl' ? 'justify-start' : 'justify-end'} gap-2`}>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setSelectedUser(user)}
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setUserToDelete(user)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filteredUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center">
                  {t('dashboard.admin.users.table.noUsers')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <UserRoleDialog
        user={selectedUser}
        open={!!selectedUser}
        onOpenChange={(open) => !open && setSelectedUser(null)}
        onUpdate={handleRoleUpdate}
      />

      <AlertDialog open={!!userToDelete} onOpenChange={() => setUserToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('dashboard.admin.users.delete.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('dashboard.admin.users.delete.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('common.delete')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add to Organization Dialog */}
      <Dialog open={!!userToAddToOrg} onOpenChange={(open) => {
        if (!open) {
          setUserToAddToOrg(null);
          setSelectedOrgId('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('dashboard.admin.users.organizations.addToOrgTitle')}: {userToAddToOrg?.firstNameEn} {userToAddToOrg?.lastNameEn}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('dashboard.admin.users.organizations.addToOrgDesc')}
            </p>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('dashboard.admin.users.organizations.selectOrganization')}</label>
              <select 
                className="w-full p-2 border rounded-md"
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
              >
                <option value="">{t('dashboard.admin.users.organizations.selectOrgPlaceholder')}</option>
                {availableOrganizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setUserToAddToOrg(null);
                setSelectedOrgId('');
              }}>
                {t('common.cancel')}
              </Button>
              <Button 
                className="bg-[#7dda55] hover:bg-[#6dc447] text-white"
                onClick={handleAddToOrganization}
                disabled={!selectedOrgId || isAddingToOrg}
              >
                {isAddingToOrg ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t('dashboard.admin.users.organizations.adding')}
                  </>
                ) : (
                  t('dashboard.admin.users.organizations.addToOrganization')
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}